/**
 * Theme and Palette Types for Cross-Platform Design System.
 * Defines shared semantic tokens, platform-specific palettes, and standard Material UI / Material Design 3 definitions.
 */

/**
 * Full color palette interface supporting both Archival Heirloom tokens
 * and standard Material UI (MUI) / Material Design 3 (M3) definitions.
 */
export interface IThemePalette {
  // --- Core / Archival Semantic Tokens ---
  text: string;
  textMuted: string;
  textSecondary: string;
  background: string;
  backgroundElement: string;
  backgroundSelected: string;
  card: string;
  border: string;
  tint: string;
  tintInactive: string;
  icon: string;
  tabIconDefault: string;
  tabIconSelected: string;
  button: string;
  buttonSecondary: string;
  buttonSecondaryBorder: string;
  buttonSecondaryText: string;
  buttonDisabled: string;
  buttonText: string;
  buttonHoverBorder: string;
  buttonSecondaryHoverBorder: string;
  buttonDangerHoverBorder: string;
  buttonDangerText: string;
  danger: string;
  header: string;
  headerText: string;
  inputEditable: string;
  inputEditableBorder: string;
  inputReadOnly: string;
  inputReadOnlyBorder: string;
  inputReadOnlyText: string;
  /** Foreground text color for editable inputs (high-contrast onSurface charcoal ink in light, ivory in dark) */
  inputText: string;
  inputPlaceholder: string;
  featuredHighlight: string;
  featuredHighlightBg: string;
  transparent: string;
  iconBackground: string;

  // --- Standard Material UI / Material Design 3 Tokens ---
  primary: string;
  onPrimary: string;
  primaryContainer: string;
  onPrimaryContainer: string;
  secondary: string;
  onSecondary: string;
  secondaryContainer: string;
  onSecondaryContainer: string;
  surface: string;
  onSurface: string;
  surfaceVariant: string;
  onSurfaceVariant: string;
  surfaceContainer: string;
  surfaceContainerHigh: string;
  outline: string;
  outlineVariant: string;
  error: string;
  onError: string;
  errorContainer: string;
  onErrorContainer: string;
  inverseSurface: string;
  inverseOnSurface: string;
  inversePrimary: string;
}

/**
 * Platform theme bundle with light and dark mode palettes.
 */
export interface IPlatformTheme {
  name: "web" | "ios" | "android";
  light: IThemePalette;
  dark: IThemePalette;
}

/**
 * Material UI Elevation definition for shadow and elevation styles.
 */
export interface IMaterialElevation {
  elevation: number;
  shadowColor: string;
  shadowOffset: { width: number; height: number };
  shadowOpacity: number;
  shadowRadius: number;
}

/**
 * Design typography schema specification.
 */
export interface IDesignTypography {
  /** Core primary sans-serif font family (Inter) */
  fontFamily: string;
  /** Primary sans-serif font stack */
  sans: string;
  /** Archival editorial serif font stack */
  serif: string;
  /** Monospaced archival stamp font stack */
  mono: string;
}

/**
 * Comprehensive Design Schema definition for the Memories design system.
 */
export interface IDesignSchema {
  /** Core primary font family name */
  fontFamily: string;
  /** Typography definitions */
  typography: IDesignTypography;
}

